/**
 * Phase 129 (Plan 129-05) — TanStack Query hooks for the project Engineering tab.
 *
 * Query keys: ['project-engineering', projectId, part]. Mutations are server-confirm only (no
 * optimistic update): a link starts Factory provisioning, so the card shows what the server says.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchProjectEngineeringAccess,
  fetchProjectGithubAvailable,
  fetchProjectRobotView,
  generateOnboardingDraft,
  linkProjectGithubRepository,
  retryProjectRepositoryProvisioning,
  saveProjectQueueConfig,
  unlinkProjectGithubRepository,
  type ProjectEngineeringAccess,
  type ProjectEngineeringQueueConfigInput,
  type ProjectRobotView,
} from '../services/api/projectEngineering';
import type { GithubRepository } from '../services/api/projectRepositories';

export function projectEngineeringKey(
  projectId: number | undefined,
  part: string,
): readonly ['project-engineering', number | null, string] {
  return ['project-engineering', projectId ?? null, part] as const;
}

function validId(projectId: number | undefined): projectId is number {
  return projectId !== undefined && Number.isFinite(projectId) && projectId > 0;
}

/** Team membership probe: success = member, a 403 error = not a member. */
export function useProjectEngineeringAccess(projectId: number | undefined, enabled: boolean) {
  return useQuery<ProjectEngineeringAccess>({
    queryKey: projectEngineeringKey(projectId, 'access'),
    queryFn: () => fetchProjectEngineeringAccess(projectId as number),
    enabled: enabled && validId(projectId),
  });
}

export function useProjectGithubAvailable(projectId: number | undefined, enabled: boolean) {
  return useQuery<GithubRepository[]>({
    queryKey: projectEngineeringKey(projectId, 'github-available'),
    queryFn: () => fetchProjectGithubAvailable(projectId as number),
    enabled: enabled && validId(projectId),
  });
}

function useInvalidateAfterRepositoryChange(projectId: number) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['project-repositories', projectId] });
    queryClient.invalidateQueries({ queryKey: ['project-repositories', 'available'] });
    queryClient.invalidateQueries({ queryKey: ['project-engineering-stats', projectId] });
    // A link starts Factory provisioning, which the Robot card shows.
    queryClient.invalidateQueries({ queryKey: ['project-engineering', projectId] });
  };
}

export function useLinkProjectGithubRepository(projectId: number) {
  const invalidate = useInvalidateAfterRepositoryChange(projectId);
  return useMutation({
    mutationFn: (githubRepositoryId: string) =>
      linkProjectGithubRepository(projectId, githubRepositoryId),
    onSuccess: invalidate,
  });
}

export function useUnlinkProjectGithubRepository(projectId: number) {
  const invalidate = useInvalidateAfterRepositoryChange(projectId);
  return useMutation({
    mutationFn: (githubRepositoryId: string) =>
      unlinkProjectGithubRepository(projectId, githubRepositoryId),
    onSuccess: invalidate,
  });
}

/** How often the Robot view refetches while a repository provisioning is pending. */
const PROVISIONING_REFETCH_MS = 5000;

/**
 * The project's Robot settings. Polls while any repository provisioning is pending so a Factory
 * project created in the background shows up without a reload; with nothing pending, no polling.
 */
export function useProjectRobotView(projectId: number | undefined) {
  return useQuery<ProjectRobotView>({
    queryKey: projectEngineeringKey(projectId, 'robot'),
    queryFn: () => fetchProjectRobotView(projectId as number),
    enabled: validId(projectId),
    refetchInterval: (query) =>
      query.state.data?.config?.repositoryProvisioning.some((e) => e.status === 'pending')
        ? PROVISIONING_REFETCH_MS
        : false,
  });
}

/** Server-confirm only: the view is refetched on success, never patched optimistically. */
export function useSaveProjectQueueConfig(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectEngineeringQueueConfigInput) =>
      saveProjectQueueConfig(projectId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectEngineeringKey(projectId, 'robot') });
    },
  });
}

export function useRetryProjectProvisioning(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (repository: string) => retryProjectRepositoryProvisioning(projectId, repository),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectEngineeringKey(projectId, 'robot') });
    },
  });
}

/** Read-only draft generation: nothing changes server-side, so nothing is invalidated. */
export function useGenerateOnboardingDraft(projectId: number) {
  return useMutation({
    mutationFn: (repository: string) => generateOnboardingDraft(projectId, repository),
  });
}
